import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Slide,
  TextField,
  MenuItem,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { UPDATE_CLIENT_LEGAL_STATUS } from 'src/store/actions/clientActions';
import { ClientLegalStatus } from 'src/utils/constants/clients';
import makeInitialState from './initialState';
import validationSchema from './schema';
import useStyles from './styles';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const LegalStatusDialog = ({ open, handleClose, submit, legalStatusData }) => {
  const { classes } = useStyles();
  const initialState = makeInitialState({ legalStatus: legalStatusData });
  const resetRef = useRef();
  const { t } = useTranslation();
  const loading = useSelector(
    isLoadingSelector([UPDATE_CLIENT_LEGAL_STATUS.action])
  );

  const handleSubmitLegalStatus = useCallback(
    values => {
      submit(values);
    },
    [submit]
  );

  useEffect(() => {
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, open]);

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      classes={{ paper: classes.paper }}
      fullWidth
      open={open}
      TransitionComponent={Transition}
      keepMounted
    >
      <Formik
        enableReinitialize
        initialValues={initialState}
        validationSchema={validationSchema}
        onSubmit={handleSubmitLegalStatus}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          resetForm
        }) => {
          resetRef.current = () => resetForm();
          const legalStatusError = Boolean(
            touched.legalStatus && errors.legalStatus
          );
          const descriptionError = Boolean(
            touched.description && errors.description
          );

          return (
            <form onSubmit={handleSubmit}>
              <DialogTitle
                className={classes.header}
                id="legal-status-dialog-title"
                component="div"
              >
                <Box>
                  <Typography variant="h3" className={classes.title}>
                    {t('CLIENTS_VIEW.CLIENT_DETAIL.EDIT_LEGAL_STATUS')}
                  </Typography>
                </Box>
                <IconButton
                  key="close"
                  aria-label="Close"
                  color="inherit"
                  onClick={handleClose}
                  className={classes.closeIcon}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent className={classes.content}>
                <Box className={classes.field}>
                  <Typography
                    variant="caption"
                    component="label"
                    htmlFor="legalStatus"
                    className={clsx(
                      classes.fieldLabel,
                      legalStatusError && classes.fieldLabelError
                    )}
                  >
                    {t('CLIENTS_VIEW.CLIENT_DETAIL.LEGAL_STATUS')}
                  </Typography>
                  <TextField
                    id="legalStatus"
                    select
                    error={legalStatusError}
                    fullWidth
                    name="legalStatus"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.legalStatus}
                    variant="outlined"
                  >
                    {Object.values(ClientLegalStatus).map(status => (
                      <MenuItem key={status} value={status}>
                        {t(
                          `CLIENTS_VIEW.CLIENT_DETAIL.LEGAL_STATUS_${status}`
                        )}
                      </MenuItem>
                    ))}
                  </TextField>
                  {legalStatusError && (
                    <Typography variant="caption" className={classes.errorText}>
                      <span className={classes.errorDot} />
                      {t(errors.legalStatus)}
                    </Typography>
                  )}
                </Box>

                <Box className={classes.field}>
                  <Typography
                    variant="caption"
                    component="label"
                    htmlFor="description"
                    className={clsx(
                      classes.fieldLabel,
                      descriptionError && classes.fieldLabelError
                    )}
                  >
                    {t('CLIENTS_VIEW.CLIENT_DETAIL.LEGAL_STATUS_CHANGE_REASON')}
                  </Typography>
                  <TextField
                    id="description"
                    error={descriptionError}
                    fullWidth
                    multiline
                    minRows={4}
                    name="description"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.description}
                    variant="outlined"
                  />
                  {descriptionError && (
                    <Typography variant="caption" className={classes.errorText}>
                      <span className={classes.errorDot} />
                      {t(errors.description)}
                    </Typography>
                  )}
                </Box>
              </DialogContent>

              <DialogActions className={classes.actions}>
                <Button
                  className={clsx(classes.button, classes.secondaryButton)}
                  disabled={loading}
                  onClick={handleClose}
                  type="button"
                >
                  {t('COMMON.CANCEL')}
                </Button>
                <Button
                  className={clsx(classes.button, classes.primaryButton)}
                  disabled={loading || values.legalStatus === legalStatusData}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                  type="submit"
                >
                  {loading ? t('COMMON.SAVING') : t('COMMON.SAVE')}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

LegalStatusDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  legalStatusData: PropTypes.string
};

export default LegalStatusDialog;
